/* azsvc — agency code (not part of Dawn).
 * <azsvc-bundle-selector> + <azsvc-sticky-atc>
 * Depends on Dawn globals loaded with defer in <head>: constants.js (PUB_SUB_EVENTS),
 * pubsub.js (publish/subscribe), global.js (fetchConfig), window.routes.
 * Blueprint: docs/03-theme-bundle-implementation-blueprint.md §1–§3.
 */
(() => {
  if (customElements.get('azsvc-bundle-selector')) return;

  /* ---------- helpers ---------- */

  // Dawn has no Shopify.formatMoney → minimal formatter for the common formats.
  function formatMoney(cents, format) {
    const fmt = format || '${{amount}}';
    const value = Number(cents || 0) / 100;
    const m = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    const key = m ? m[1] : 'amount';
    let out;
    switch (key) {
      case 'amount_no_decimals':
        out = Math.round(value).toLocaleString('en-US');
        break;
      case 'amount_with_comma_separator':
        out = value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = Math.round(value).toLocaleString('de-DE');
        break;
      default:
        out = value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, out);
  }

  const getCartElement = () =>
    document.querySelector('cart-drawer') || document.querySelector('cart-notification');

  class AzsvcCartError extends Error {
    constructor(payload, status) {
      super(
        payload?.description ||
          (typeof payload?.errors === 'string' ? payload.errors : null) ||
          payload?.message ||
          'Could not add to cart.'
      );
      this.payload = payload;
      this.status = status;
    }
  }

  async function fetchCart() {
    const res = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    return res.json();
  }

  // Multi-line add via /cart/add.js, asking Shopify to render the cart drawer sections (≤5).
  async function addItemsToCart(items, { render = true } = {}) {
    const cart = getCartElement();
    const sectionIds =
      cart && typeof cart.getSectionsToRender === 'function'
        ? cart.getSectionsToRender().map((s) => s.section || s.id)
        : [];

    const body = { items };
    if (sectionIds.length) {
      body.sections = [...new Set(sectionIds)].slice(0, 5).join(',');
      body.sections_url = window.location.pathname;
    }

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.body = JSON.stringify(body);

    const res = await fetch(window.routes.cart_add_url, config);
    let json = null;
    try {
      json = await res.json();
    } catch (_) {
      /* empty body */
    }

    if (!res.ok || json?.status) {
      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'azsvc-bundle',
          productVariantId: items[0]?.id,
          errors: json?.description || json?.errors || json?.message,
          message: json?.message,
        });
      }
      throw new AzsvcCartError(json || {}, res.status);
    }

    if (render && cart && json.sections) {
      if (cart.tagName === 'CART-NOTIFICATION') {
        const first = json.items?.[0];
        cart.renderContents({ ...json, id: first?.variant_id, key: first?.key });
      } else {
        // Dawn's product-form does this too: a drawer rendered while the cart was empty keeps
        // .is-empty (no padding, hidden header, grid layout) unless we clear it before rendering.
        cart.classList.remove('is-empty');
        cart.renderContents(json); // CartDrawer.renderContents replaces HTML and open()s the drawer
      }
    } else if (render && !cart) {
      window.location = window.routes.cart_url; // cart_type = page
    }

    if (typeof publish === 'function') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'azsvc-bundle', productVariantId: items[0]?.id, cartData: json });
    }
    return json;
  }

  // Re-render drawer + bubble from the server without opening (used after an error / partial add).
  async function refreshCartSections() {
    const ids = ['cart-drawer', 'cart-icon-bubble'];
    try {
      const res = await fetch(`${window.routes.cart_url}?sections=${ids.join(',')}`, {
        headers: { Accept: 'application/json' },
      });
      const sections = await res.json();
      const parse = (html, sel) => new DOMParser().parseFromString(html, 'text/html').querySelector(sel);
      if (sections['cart-drawer']) {
        const fresh = parse(sections['cart-drawer'], '#CartDrawer');
        const current = document.querySelector('#CartDrawer');
        if (fresh && current) current.innerHTML = fresh.innerHTML;
        const drawer = document.querySelector('cart-drawer');
        const freshDrawer = parse(sections['cart-drawer'], 'cart-drawer');
        if (drawer && freshDrawer) drawer.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
      }
      if (sections['cart-icon-bubble']) {
        const fresh = parse(sections['cart-icon-bubble'], '.shopify-section');
        const current = document.getElementById('cart-icon-bubble');
        if (fresh && current) current.innerHTML = fresh.innerHTML;
      }
    } catch (_) {
      /* non-blocking */
    }
  }

  /* ---------- <azsvc-bundle-selector> ---------- */

  class AzsvcBundleSelector extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.submitBtn = this.form.querySelector('[type="submit"]');
      this.submitLabel = this.form.querySelector('[data-submit-label]');
      this.errorEl = this.form.querySelector('[data-error]');
      this.statusEl = this.form.querySelector('[data-status]');
      this.variantSelect = this.form.querySelector('[data-variant-select]');
      this.moneyFormat = this.dataset.moneyFormat;
      this.variantId = Number(this.dataset.variantId);
      this.price = Number(this.dataset.price);
      this.compare = Number(this.dataset.compare || 0);
      this.available = this.dataset.available === 'true';

      this.onSubmit = this.onSubmit.bind(this);
      this.onChange = this.onChange.bind(this);
      this.form.addEventListener('submit', this.onSubmit);
      this.form.addEventListener('change', this.onChange);

      // Keep in sync if a Dawn product section on the same page changes variant.
      if (typeof subscribe === 'function') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const v = event?.data?.variant;
          if (!v || String(v.product_id || '') !== String(this.dataset.productId)) return;
          this.setVariant(v.id, v.price, v.compare_at_price || 0, v.available);
        });
      }

      this.render();
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit);
      this.form?.removeEventListener('change', this.onChange);
      this.unsubscribe?.();
    }

    get selected() {
      return this.form.querySelector('.azsvc-tier__input:checked') || this.form.querySelector('.azsvc-tier__input');
    }

    setVariant(id, price, compare, available) {
      this.variantId = Number(id);
      this.price = Number(price);
      this.compare = Number(compare || 0);
      this.available = Boolean(available);
      this.render();
    }

    onChange(event) {
      if (event.target === this.variantSelect) {
        const opt = this.variantSelect.selectedOptions[0];
        this.setVariant(opt.value, opt.dataset.price, opt.dataset.compare, opt.dataset.available === 'true');
        return;
      }
      this.render();
    }

    // Advertising price for one tier (cents). Real price = Shopify automatic discount.
    priceFor(input) {
      const qty = Math.max(1, Number(input.dataset.qty || 1));
      if (input.dataset.mode === 'pack') {
        const price = Number(input.dataset.price || 0);
        const compare = Number(input.dataset.compare || 0);
        return { qty, total: price, was: Math.max(compare, price), perUnit: price };
      }
      const pct = Number(input.dataset.pct || 0);
      const full = this.price * qty;
      const total = Math.round((full * (100 - pct)) / 100);
      // Saving vs regular price only (not compare-at) so it equals the real cart discount.
      return { qty, total, was: full, perUnit: Math.round(total / qty) };
    }

    render() {
      const fmt = (c) => formatMoney(c, this.moneyFormat);
      this.form.querySelectorAll('.azsvc-tier__input').forEach((input) => {
        const row = input.closest('.azsvc-tier');
        const { qty, total, was, perUnit } = this.priceFor(input);
        const saving = was - total;
        row.querySelector('[data-total]').textContent = fmt(total);
        const wasEl = row.querySelector('[data-was]');
        const saveEl = row.querySelector('[data-save]');
        wasEl.hidden = saving <= 0;
        wasEl.textContent = fmt(was);
        saveEl.hidden = saving <= 0;
        saveEl.textContent = `Save ${fmt(saving)}`;
        const unitEl = row.querySelector('[data-unit]');
        if (unitEl) unitEl.textContent = qty > 1 ? `${fmt(perUnit)} / each` : '';
        const shipEl = row.querySelector('[data-ship]');
        const shipAt = Number(this.dataset.shipThreshold || 0);
        if (shipEl) shipEl.hidden = !(shipAt > 0 && total >= shipAt);
      });

      const sel = this.selected;
      if (!sel) return;
      const { total } = this.priceFor(sel);
      const addText = window.variantStrings?.addToCart || 'Add to cart';
      const soldText = window.variantStrings?.soldOut || 'Sold out';
      this.submitBtn.disabled = !this.available;
      this.submitLabel.textContent = this.available ? `${addText} · ${fmt(total)}` : soldText;

      this.dispatchEvent(
        new CustomEvent('azsvc:offer-change', {
          bubbles: true,
          detail: { label: sel.dataset.label, price: fmt(total), available: this.available },
        })
      );
    }

    buildItems(input) {
      const qty = Math.max(1, Number(input.dataset.qty || 1));
      if (input.dataset.mode === 'pack') {
        const id = Number(input.dataset.variant) || this.variantId;
        return [
          {
            id,
            quantity: 1,
            properties: { _bundle_id: `pack:${input.value}`, _bundle_role: 'main' },
          },
        ];
      }
      // Deterministic _bundle_id → adding again merges into the same line (blueprint §6.1).
      return [
        {
          id: this.variantId,
          quantity: qty,
          properties: { _bundle_id: `qty:${this.dataset.productHandle}`, _bundle_role: 'main' },
        },
      ];
    }

    // Best advertised tier for a cart quantity (automatic discounts use "minimum quantity ≥").
    expectedPctFor(cartQty) {
      let best = 0;
      this.form.querySelectorAll('.azsvc-tier__input[data-mode="quantity"]').forEach((i) => {
        if (cartQty >= Number(i.dataset.qty)) best = Math.max(best, Number(i.dataset.pct || 0));
      });
      return best;
    }

    async onSubmit(event) {
      event.preventDefault();
      const input = this.selected;
      if (!input || this.submitBtn.disabled || this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      this.setLoading(true);
      this.showError('');
      this.showStatus('');
      let added = false;
      try {
        await addItemsToCart(this.buildItems(input), {
          render: this.dataset.openDrawer !== 'false' && !window.Shopify?.designMode,
        });
        added = true;
      } catch (err) {
        this.showError(err.message);
        // Docs do not promise all-or-nothing → re-read the cart and resync the drawer.
        await refreshCartSections();
      }

      // Show REAL totals from the cart (never the advertised %) — blueprint §3.4.
      try {
        const cart = await fetchCart();
        this.showCartTruth(cart, added);
      } catch (_) {
        /* ignore */
      } finally {
        this.setLoading(false);
      }
    }

    showCartTruth(cart, added) {
      const fmt = (c) => formatMoney(c, this.moneyFormat);
      const lines = (cart.items || []).filter((i) => String(i.product_id) === String(this.dataset.productId));
      if (!lines.length) return;
      const qty = lines.reduce((s, i) => s + i.quantity, 0);
      const original = lines.reduce((s, i) => s + (i.original_line_price ?? i.price * i.quantity), 0);
      const final = lines.reduce((s, i) => s + (i.final_line_price ?? i.line_price), 0);
      const discount = original - final;
      const titles = [
        ...new Set(lines.flatMap((i) => (i.discounts || []).map((d) => d.title)).filter(Boolean)),
      ];

      const pct = this.expectedPctFor(qty);
      const expectedDiscount = lines.reduce(
        (s, i) => s + Math.round(((i.original_line_price ?? i.price * i.quantity) * pct) / 100),
        0
      );

      let msg = `${added ? 'Added. ' : ''}In your cart: ${qty} × ${lines[0].product_title} — ${fmt(final)}`;
      if (discount > 0) {
        msg += ` (you save ${fmt(discount)}${titles.length ? ` · ${titles.join(', ')}` : ''})`;
      }
      if (Math.abs(discount - expectedDiscount) > qty) {
        // Admin discounts do not match what the selector advertises → stay neutral, log for the agency.
        msg = `${added ? 'Added. ' : ''}In your cart: ${qty} × ${lines[0].product_title}. Discounts are confirmed at checkout.`;
        console.warn('[azsvc] advertised discount mismatch', { qty, pct, expectedDiscount, discount });
      }
      this.showStatus(msg);
    }

    setLoading(on) {
      this.submitBtn.classList.toggle('loading', on);
      this.submitBtn.setAttribute('aria-disabled', String(on));
      this.submitBtn.querySelector('.loading__spinner')?.classList.toggle('hidden', !on);
    }

    showError(msg) {
      this.errorEl.hidden = !msg;
      this.errorEl.textContent = msg || '';
    }

    showStatus(msg) {
      this.statusEl.hidden = !msg;
      this.statusEl.textContent = msg || '';
    }
  }

  customElements.define('azsvc-bundle-selector', AzsvcBundleSelector);

  /* ---------- <azsvc-sticky-atc> ---------- */

  class AzsvcStickyAtc extends HTMLElement {
    connectedCallback() {
      this.selector = document.getElementById(this.dataset.target);
      if (!this.selector) return;
      this.hidden = false;
      this.labelEl = this.querySelector('[data-sticky-label]');
      this.priceEl = this.querySelector('[data-sticky-price]');
      this.cta = this.querySelector('[data-sticky-cta]');

      this.onOffer = (e) => {
        this.labelEl.textContent = e.detail.label || '';
        this.priceEl.textContent = e.detail.price || '';
        this.cta.disabled = !e.detail.available;
      };
      this.selector.addEventListener('azsvc:offer-change', this.onOffer);
      this.selector.render?.();

      this.onClick = () => {
        const form = this.selector.querySelector('form');
        if (form?.requestSubmit) form.requestSubmit();
        else form?.dispatchEvent(new Event('submit', { cancelable: true }));
      };
      this.cta.addEventListener('click', this.onClick);

      const button = this.selector.querySelector('[type="submit"]');
      this.observer = new IntersectionObserver(([entry]) => {
        // Show only once the real button has scrolled ABOVE the viewport.
        const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', past);
        this.setAttribute('aria-hidden', String(!past));
        this.cta.tabIndex = past ? 0 : -1;
      });
      this.observer.observe(button);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.selector?.removeEventListener('azsvc:offer-change', this.onOffer);
      this.cta?.removeEventListener('click', this.onClick);
    }
  }

  if (!customElements.get('azsvc-sticky-atc')) customElements.define('azsvc-sticky-atc', AzsvcStickyAtc);

  // Theme editor: selecting a block/section re-inits via connectedCallback; nothing else needed.
})();
